/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react'
import { useSelector } from 'react-redux'

import { userClient } from '../clients/UserClient'
import { User } from '../models/client/user'
import { getCompanyAction } from '../store/company'
import { useReduxDispatch } from '../store/index'
import { loadMetadataAction } from '../store/metadata'
import { getAllSitesAction } from '../store/sites'
import { selectUser, selectUserInit, logoutAction, addUser } from '../store/user'
import { ApiError } from '../util/errors/ApiError'
import { isEqual } from '../util/isEqual'
import analytics from '../util/analytics'

import { useAuthentication, LoginFn } from './useAuthentication'
import { usePrevious } from './usePrevious'

// import { useError } from './useErrorBoundary'

type Params = {
  onChange?: (user?: User) => void
  onError?: (error: Error) => void
}

export const useAuthWatcher = (
  params?: Params
): { login: LoginFn; logout: () => void; isAuthed: boolean } => {
  const dispatch = useReduxDispatch()
  const { token, userId, logout, login } = useAuthentication()
  // const error = useError()
  const user = useSelector(selectUser, isEqual)
  const isInit = useSelector(selectUserInit, isEqual)
  const previousUser = usePrevious<User | null | undefined>(user)

  useEffect(() => {
    const initApp = async (): Promise<void> => {
      try {
        if (!token || !userId) {
          throw new ApiError({ type: 'Unauthorized' })
        }
        const nextUser = await userClient.loadSelf({ token, userId })
        dispatch(addUser(nextUser))
        const { companyId } = nextUser
        const nextCompany = await dispatch(getCompanyAction({ token, companyId }))
        const { payload: nextCompanyPayload } = nextCompany
        await dispatch(getAllSitesAction({ token, companyId }))
        await dispatch(loadMetadataAction({ token }))

        analytics.setUser({
          id: nextUser.id,
          email: nextUser.email,
          name: nextUser.name,
          companyId: nextUser.companyId,
          companyName: nextCompanyPayload?.name,
        })
      } catch (e) {
        params?.onChange?.()

        if (e instanceof Error) params?.onError?.(e)

        // console.warn('NOTE: this never gets called oddly', e)
        // NOTE: never reached, even though error is thrown in `loadSelf` when unauthorized
        // await dispatch(logoutAction)
        // window.location.href = '/auth'
      }
    }
    if (!isInit) initApp()
  }, [])

  useEffect(() => {
    if (!previousUser && user) params?.onChange?.(user)
    if (previousUser && !user) params?.onChange?.()
  }, [user, params])

  // useEffect(() => {
  //     const handleAsync = async (): Promise<void> => {
  //         if (error instanceof ApiError && error.type === 'Unauthorized') {
  //             await logout()
  //             params?.onError?.(error)
  //             params?.onChange?.()
  //         }
  //     }
  //     handleAsync()
  // }, [error])

  return {
    logout,
    login,
    isAuthed: !!token,
  }
}
